import { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Draggable from 'react-draggable';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveIcon from '@mui/icons-material/Remove';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Card,
  Typography,
  Button,
  CardMedia,
  Avatar,
  Stack,
  Slide,
  IconButton,
  Grid
} from '@material-ui/core';
import CardHeader from '@material-ui/core/Card';
import LoadingSkeleton from './LoadingSkeleton';

const helpSiteBase = '/static/help-system-guide/';

const defaultHelpTopic = 'virtual_rack_home.html';

function topicPathFromStep(step) {
  if (!step) {
    return defaultHelpTopic;
  }

  const topic = step
    .replace(/^\//, '')
    .replace(/\.htm$/i, '.html')
    .replace(/\.md$/i, '.html');
  return topic.endsWith('.html') ? topic : `${topic}.html`;
}

const avatarStyle = {
  position: 'fixed',
  zIndex: '99999',
  bottom: '20px',
  right: '20px',
  padding: 0,
  margin: 0,
  minWidth: 40,
  height: 40,
  backgroundColor: 'primary.light',
  '&:hover': { backgroundColor: 'primary.main' }
};

const cardStyle = {
  paddingLeft: 0,
  paddingRight: 0,
  position: 'fixed',
  zIndex: '99999',
  right: '20px',
  height: '680px',
  width: '450px',
  maxHeight: '90vh',
  bottom: '20px',
  overflow: 'hidden',
  border: '1px solid #d8e2ea',
  boxShadow: '0 18px 42px rgba(34, 47, 62, 0.18)'
};

export default function HelpSystem() {
  const [currentDocument, setCurrentDocument] = useState(defaultHelpTopic);
  const [searchParams] = useSearchParams();
  const [currX, setCurrX] = useState(0);
  const [currWindowWidth, setCurrWindowWidth] = useState(window.innerWidth);
  const [collapsed, setCollapsed] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const location = useLocation();

  const handleWindowResize = () => {
    setCurrX(0);
    setCurrWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    const setHelpPage = () => {
      // Set current document based on page the user is on
      if (location.search !== ' ') {
        if (searchParams.get('ssh_open')) {
          setCurrentDocument('connect_to_vrack.html');
        } else if (searchParams.get('new_component')) {
          setCurrentDocument('Loading/add_new_component.html');
        } else if (searchParams.get('loading_open')) {
          setCurrentDocument('Loading/load_vrack.html');
        } else if (searchParams.get('step')) {
          setCurrentDocument(topicPathFromStep(searchParams.get('step')));
        } else if (searchParams.get('flight_data_open')) {
          setCurrentDocument('aircraft_data.html');
        } else if (location.pathname) {
          if (location.pathname.startsWith('/snapshots')) {
            setCurrentDocument('snapshots.html');
          } else if (location.pathname.startsWith('/vmtemplates')) {
            setCurrentDocument('vm_templates.html');
          } else if (location.pathname.startsWith('/teams')) {
            setCurrentDocument('team_management.html');
          } else if (location.pathname.includes('/racks/details')) {
            setCurrentDocument('vrack_details.html');
          } else if (location.pathname.includes('/racks')) {
            setCurrentDocument(defaultHelpTopic);
          } else if (
            location.pathname.includes('/team/upload') ||
            location.pathname.includes('/upload')
          ) {
            setCurrentDocument('Loading/loadables.html');
          }
        }
      }
    };

    if (location !== null) {
      setHelpPage();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [location]);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const helpUrl = `${helpSiteBase}${currentDocument}`;
  const embeddedHelpUrl = `${helpUrl.replace(/\.html$/, '.htm')}?embed=1`;

  return (
    <Stack direction="row" justifyContent="flex-end" alignItems="flex-end">
      {collapsed && (
        <Avatar
          data-testid="help-button"
          component={Button}
          onClick={() => {
            setCollapsed(false);
            setLoaded(false);
          }}
          sx={avatarStyle}
        >
          <HelpOutlineIcon fontSize="large" />
        </Avatar>
      )}
      <Draggable
        onStop={(e, data) => {
          setCurrX(data.lastX);
        }}
        position={{ x: currX, y: 0 }}
        axis="x"
        bounds={{ left: -(currWindowWidth - parseInt(cardStyle.width, 10) - 20), right: 0 }}
      >
        <Slide direction="up" in={!collapsed} mountOnEnter unmountOnExit>
          <Card sx={cardStyle}>
            <Grid container direction="column" sx={{ height: '100%' }}>
              <Grid item>
                <CardHeader
                  sx={{
                    backgroundColor: '#f5f7f9',
                    borderBottom: '3px solid #0072bc',
                    padding: 1
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ paddingLeft: 1 }} variant="h6">
                      Virtual Rack Help
                    </Typography>
                    <Stack direction="row" alignItems="center">
                      <IconButton
                        onClick={() => {
                          window.open(helpUrl);
                        }}
                        aria-label="maximize"
                      >
                        <OpenInNewIcon />
                      </IconButton>

                      <IconButton
                        onClick={() => {
                          setCollapsed(true);
                          setCurrX(0);
                        }}
                        aria-label="minimize"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardHeader>
              </Grid>

              <Grid item xs sx={{ overflow: 'auto' }}>
                {!loaded && <LoadingSkeleton />}
                <CardMedia
                  component="iframe"
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: !loaded ? 'none' : '',
                    borderLeft: '3px solid #0072bc',
                    borderRight: '3px solid #0072bc',
                    borderBottom: '3px solid #0072bc',
                    boxSizing: 'border-box'
                  }}
                  onLoad={() => {
                    setLoaded(true);
                  }}
                  image={embeddedHelpUrl}
                />
              </Grid>
            </Grid>
          </Card>
        </Slide>
      </Draggable>
    </Stack>
  );
}
